import { ref } from "vue";
import axios from "axios";
import { PhotoItem } from "seedgreen-shared/types/photo.types";
import { PhotoUploadUrlsResponseDTO } from "seedgreen-shared/dtos/PhotoDTO";
import { compressImage } from "@/utils/photos";

export interface PhotoWithUploadUrls {
	photo: PhotoItem;
	uploadUrls: PhotoUploadUrlsResponseDTO;
}

export interface UploadedPhoto {
	photoId: string;
	filename: string;
	fullSizeUrl: string;
	thumbnailUrl: string | null;
}

/**
 * Composable for uploading photos directly to Azure Blob Storage.
 * Does NOT make API calls - expects signed URLs to be provided.
 *
 * Note: Photos should already be compressed before reaching this composable (e.g., in ImageUpload).
 * This composable will upload the compressed file as-is for full-size, and compress further for thumbnails.
 *
 * Use this composable in conjunction with mutation hooks like useGetNotePhotoUploadUrls().
 */
export function usePhotoUpload() {
	const error = ref<string | null>(null);

	// Helper to create Azure Blob Storage headers
	const createBlobHeaders = (contentType: string) => ({
		"Content-Type": contentType,
		"x-ms-blob-type": "BlockBlob",
	});

	/**
	 * Uploads photo FILES to Azure Blob Storage using provided signed URLs.
	 * Compresses images and performs the actual file upload to cloud storage.
	 * @param photosWithUrls - Array of photos paired with their upload URLs
	 * @returns Promise resolving to array of uploaded photo info with public URLs
	 */
	const uploadPhotosToAzure = async (
		photosWithUrls: PhotoWithUploadUrls[],
	): Promise<UploadedPhoto[]> => {
		if (photosWithUrls.length === 0) return [];

		try {
			error.value = null;

			const uploadedPhotos: UploadedPhoto[] = [];

			// Upload each photo individually
			for (const { photo, uploadUrls: uploadUrl } of photosWithUrls) {
				try {
					// photo.file is already compressed in ImageUpload - use as-is for full-size
					await axios.put(uploadUrl.fullSizeUploadUrl, photo.file, {
						headers: createBlobHeaders(photo.file.type),
					});

					// If there's a separate thumbnail URL, compress further for thumbnail
					if (
						uploadUrl.thumbnailUploadUrl &&
						uploadUrl.thumbnailUploadUrl !==
							uploadUrl.fullSizeUploadUrl
					) {
						const thumbnailFile = await compressImage(
							photo.file,
							0.7,
							400,
						);
						await axios.put(
							uploadUrl.thumbnailUploadUrl,
							thumbnailFile,
							{
								headers: createBlobHeaders(thumbnailFile.type),
							},
						);
					}

					uploadedPhotos.push({
						photoId: uploadUrl.photoId,
						filename: photo.name, // Use original filename
						fullSizeUrl: uploadUrl.fullSizePublicUrl,
						thumbnailUrl: uploadUrl.thumbnailPublicUrl,
					});
				} catch (uploadError) {
					console.error(
						`Failed to upload photo ${photo.name}:`,
						uploadError,
					);
					throw uploadError;
				}
			}

			return uploadedPhotos;
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to upload photos";
			error.value = errorMessage;
			// Re-throw the original error to preserve stack trace
			throw err;
		}
	};

	/**
	 * Uploads photos to Azure in the background (fire-and-forget).
	 * Useful for non-blocking uploads after form submission.
	 * Properly handles async callbacks to prevent unhandled rejections.
	 * @param photosWithUrls - Array of photos paired with their upload URLs
	 * @param onSuccess - Callback when all uploads complete (can be async)
	 * @param onError - Callback if upload fails or if onSuccess callback throws
	 */
	const uploadPhotosToAzureInBackground = async (
		photosWithUrls: PhotoWithUploadUrls[],
		onSuccess?: (uploadedPhotos: UploadedPhoto[]) => void | Promise<void>,
		onError?: (error: Error) => void,
	): Promise<void> => {
		if (photosWithUrls.length === 0) return;

		try {
			const uploadedPhotos = await uploadPhotosToAzure(photosWithUrls);

			if (onSuccess) {
				await Promise.resolve(onSuccess(uploadedPhotos));
			}
		} catch (error) {
			onError?.(error as Error);
		}
	};

	return {
		error,
		uploadPhotosToAzure,
		uploadPhotosToAzureInBackground,
	};
}
